import { createFeature, createReducer, on } from '@ngrx/store'
import {
    addChildToSelectionRoot,
    findParentSelectionNodeInRoot,
    findSelectionNodeInRoot,
    FreeSelection,
    generateSelectionId,
    HierarchicalSelection,
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    mergeBucketsIntoTarget,
    mergePointsIntoBuckets,
    removeBucketsFromTarget,
    removeChildrenFromSelectionRoot,
    removeChildrenFromSelectionRootRecursively,
    SelectionBase,
    SelectionMode,
    SelectionState,
    SelectionType,
    UnsupervisedSelectionRoot,
    UnsupervisedSelection,
    bucketsEqual,
    clearAllBuckets,
    clearBucket,
    copyBuckets,
    ensureBucketsForSegSources,
    getHierarchicalRootSegSources,
    getTotalPointsSize,
} from './selection.state'
import { selectionActions } from './selection.actions'
import { getAnnotationColor } from '../../shared/utils/shaders'
import { caseActions } from '../case/case.actions'
import {
    HierarchicalSelectionNodeQueryParam,
    HierarchicalSelectionRootQueryParam,
} from '../../shared/model/query-params-types'
import { getPlotData } from '../plot-data/plot-data'

const createTopLevelBucketsFromPlot = () => {
    const bySeg = new Map<string, Set<number>>()
    for (const p of getPlotData().points) {
        const segSource = (p[5] as string | undefined) ?? ''
        if (!bySeg.has(segSource)) {
            bySeg.set(segSource, new Set())
        }
        bySeg.get(segSource)!.add(p[0])
    }
    return [...bySeg.entries()].map(([segSource, points]) => ({ segSource, points }))
}

const createNewSelectionBase = (): SelectionBase => ({
    id: generateSelectionId(),
    name: undefined,
    modified: false,
    buckets: [],
    annotationVisible: true,
    annotationColor: getAnnotationColor().hexString,
    annotationOpacity: 1,
})

const createNewFreeSelection = (): FreeSelection => ({
    ...createNewSelectionBase(),
    type: SelectionType.Free,
})

export const topLevelSelectionName = 'Top level'

const createNewHierarchicalSelectionRoot = (
    name: string | undefined,
    segSources: string[] = []
): HierarchicalSelection => {
    const plotBuckets = createTopLevelBucketsFromPlot()
    const lockedSegSources =
        segSources.length > 0
            ? [...new Set(segSources.filter(Boolean))].sort((a, b) => a.localeCompare(b))
            : [...new Set(plotBuckets.map(b => b.segSource).filter(Boolean))].sort((a, b) =>
                  a.localeCompare(b)
              )
    const buckets = ensureBucketsForSegSources(plotBuckets, lockedSegSources)
    return {
        id: generateSelectionId(),
        name: name,
        modified: false,
        type: SelectionType.Hierarchical,
        segSources: lockedSegSources,
        children: [
            {
                id: generateSelectionId(),
                name: topLevelSelectionName,
                buckets,
                modified: false,
                annotationVisible: true,
                annotationColor: getAnnotationColor().hexString,
                annotationOpacity: 1,
                children: [],
            },
        ],
        unsavedLevel: createNewSelectionBase(),
        selectedHierarchyName: undefined,
    }
}

const createNewUnsupervisedSelectionRoot = (
    name: string | undefined,
    segSources: string[] = []
): UnsupervisedSelection => {
    const plotBuckets = createTopLevelBucketsFromPlot()
    const lockedSegSources =
        segSources.length > 0
            ? [...new Set(segSources.filter(Boolean))].sort((a, b) => a.localeCompare(b))
            : [...new Set(plotBuckets.map(b => b.segSource).filter(Boolean))].sort((a, b) =>
                  a.localeCompare(b)
              )
    const buckets = ensureBucketsForSegSources(plotBuckets, lockedSegSources)
    return {
        id: generateSelectionId(),
        name: name,
        modified: false,
        type: SelectionType.Unsupervised,
        segSources: lockedSegSources,
        children: [
            {
                id: generateSelectionId(),
                name: topLevelSelectionName,
                buckets,
                modified: false,
                annotationVisible: true,
                annotationColor: getAnnotationColor().hexString,
                annotationOpacity: 1,
                children: [],
            },
        ],
        unsavedLevel: createNewSelectionBase(),
        selectedUnsupervisedName: undefined,
    }
}

const createInitialState = (): SelectionState => ({
    type: SelectionType.Free,
    mode: SelectionMode.Add,
    activeSegSource: undefined,
    freeSelection: createNewFreeSelection(),
    savedFreeSelections: new Map<string, FreeSelection>(),
    originalSavedFreeSelections: new Map<string, FreeSelection>(),
    hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
    savedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>(),
    originalSavedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>(),
    displayedOnScatterPlot: [],
    unsupervisedSelection: createNewUnsupervisedSelectionRoot(undefined),
    savedUnsupervisedSelections: new Map<string, UnsupervisedSelectionRoot>(),
    originalSavedUnsupervisedSelections: new Map<string, HierarchicalSelectionRoot>(),
    resolution: 6,
})

export const selectionFeature = createFeature({
    name: 'selection',
    reducer: createReducer<SelectionState>(
        createInitialState(),
        on(selectionActions.setType, (state, { selectionType }): SelectionState => {
            return {
                ...state,
                type: selectionType,
                freeSelection: createNewFreeSelection(),
                hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
                unsupervisedSelection: createNewUnsupervisedSelectionRoot(undefined),
            }
        }),
        on(selectionActions.setMode, (state, { mode }): SelectionState => {
            return {
                ...state,
                mode,
            }
        }),
        on(selectionActions.setActiveSegSource, (state, { segSource }): SelectionState => {
            if (state.activeSegSource === segSource) return state
            return { ...state, activeSegSource: segSource }
        }),
        on(selectionActions.updateSelection, (state, { segSource, points }): SelectionState => {
            if (state.type == SelectionType.Hierarchical) {
                const allowed = getHierarchicalRootSegSources(state.hierarchicalSelection)
                if (allowed.length > 0 && !allowed.includes(segSource)) {
                    return state
                }
                const newBuckets = mergePointsIntoBuckets(
                    state.hierarchicalSelection.unsavedLevel.buckets,
                    segSource,
                    points,
                    state.mode
                )
                const selectionRoot = {
                    ...state.hierarchicalSelection,
                    unsavedLevel: {
                        ...state.hierarchicalSelection.unsavedLevel,
                        buckets: newBuckets,
                    },
                }
                updateSavedHierarchicalSelection(selectionRoot, state)
                return {
                    ...state,
                    hierarchicalSelection: selectionRoot,
                }
            }
            if (state.type == SelectionType.Unsupervised) {
                const allowed = getHierarchicalRootSegSources(state.unsupervisedSelection)
                if (allowed.length > 0 && !allowed.includes(segSource)) {
                    return state
                }
                const newBuckets = mergePointsIntoBuckets(
                    state.unsupervisedSelection.unsavedLevel.buckets,
                    segSource,
                    points,
                    state.mode
                )
                const selectionRoot = {
                    ...state.unsupervisedSelection,
                    unsavedLevel: {
                        ...state.unsupervisedSelection.unsavedLevel,
                        buckets: newBuckets,
                    },
                }
                updateSavedHierarchicalSelection(selectionRoot, state)
                return {
                    ...state,
                    unsupervisedSelection: selectionRoot,
                }
            }
            const newBuckets = mergePointsIntoBuckets(
                state.freeSelection.buckets,
                segSource,
                points,
                state.mode
            )
            let modified = false

            if (state.freeSelection.name != undefined) {
                const previousSelection =
                    state.savedFreeSelections.get(state.freeSelection.name)?.buckets ?? []
                if (!bucketsEqual(previousSelection, newBuckets)) {
                    modified = true
                }
            } else if (getTotalPointsSize({ buckets: newBuckets }) > 0) {
                modified = true
            }

            return {
                ...state,
                freeSelection: {
                    ...state.freeSelection,
                    buckets: newBuckets,
                    modified: modified,
                },
            }
        }),

        on(selectionActions.clearSelection, (state, { allBuckets }): SelectionState => {
            const segSource = state.activeSegSource
            if (state.type == SelectionType.Hierarchical) {
                const newBuckets = allBuckets
                    ? clearAllBuckets(state.hierarchicalSelection.unsavedLevel.buckets)
                    : segSource
                      ? clearBucket(state.hierarchicalSelection.unsavedLevel.buckets, segSource)
                      : clearAllBuckets(state.hierarchicalSelection.unsavedLevel.buckets)
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        unsavedLevel: {
                            ...state.hierarchicalSelection.unsavedLevel,
                            buckets: newBuckets,
                        },
                    },
                }
            }
            if (state.type == SelectionType.Unsupervised) {
                const newBuckets = allBuckets
                    ? clearAllBuckets(state.unsupervisedSelection.unsavedLevel.buckets)
                    : segSource
                      ? clearBucket(state.unsupervisedSelection.unsavedLevel.buckets, segSource)
                      : clearAllBuckets(state.unsupervisedSelection.unsavedLevel.buckets)
                return {
                    ...state,
                    unsupervisedSelection: {
                        ...state.unsupervisedSelection,
                        unsavedLevel: {
                            ...state.unsupervisedSelection.unsavedLevel,
                            buckets: newBuckets,
                        },
                    },
                }
            }
            const newBuckets = allBuckets
                ? clearAllBuckets(state.freeSelection.buckets)
                : segSource
                  ? clearBucket(state.freeSelection.buckets, segSource)
                  : clearAllBuckets(state.freeSelection.buckets)
            let modified = false
            if (state.freeSelection.name != undefined) {
                const previousSelection =
                    state.savedFreeSelections.get(state.freeSelection.name)?.buckets ?? []
                if (!bucketsEqual(previousSelection, newBuckets)) {
                    modified = true
                }
            }
            return {
                ...state,
                freeSelection: {
                    ...state.freeSelection,
                    buckets: newBuckets,
                    modified: modified,
                },
            }
        }),
        on(
            selectionActions.loadingFreeSelectionsSucceeded,
            (state, { selections, queryParams }): SelectionState => {
                const savedFreeSelections = new Map(
                    Array.from(selections.entries()).map(
                        ([key, selection]: [string, FreeSelection]) => {
                            const queryParamsSelection = queryParams.get(key)
                            return [
                                key,
                                {
                                    ...selection,
                                    annotationVisible: queryParamsSelection
                                        ? queryParamsSelection.annotationVisible
                                        : selection.annotationVisible,
                                    annotationOpacity: queryParamsSelection
                                        ? queryParamsSelection.annotationOpacity
                                        : selection.annotationOpacity,
                                    annotationColor: queryParamsSelection
                                        ? queryParamsSelection.annotationColor
                                        : selection.annotationColor,
                                },
                            ]
                        }
                    )
                )
                return {
                    ...state,
                    savedFreeSelections: savedFreeSelections,
                    originalSavedFreeSelections: savedFreeSelections,
                }
            }
        ),
        on(
            selectionActions.loadingHierarchicalSelectionsSucceeded,
            (state, { selections, queryParams }): SelectionState => {
                const loadedSelections = selections
                loadedSelections.forEach((selectionRoot, key) => {
                    const queryRoot = queryParams.get(key)
                    if (queryRoot) {
                        updateSelectionRootWithQueryParams(selectionRoot, queryRoot)
                    }
                })
                return {
                    ...state,
                    savedHierarchicalSelections: loadedSelections,
                    originalSavedHierarchicalSelections: loadedSelections,
                }
            }
        ),
        on(
            selectionActions.loadingUnsupervisedSelectionsSucceeded,
            (state, { selections, queryParams }): SelectionState => {
                const loadedSelections = selections
                loadedSelections.forEach((selectionRoot, key) => {
                    const queryRoot = queryParams.get(key)
                    if (queryRoot) {
                        updateSelectionRootWithQueryParams(selectionRoot, queryRoot)
                    }
                })
                return {
                    ...state,
                    savedUnsupervisedSelections: loadedSelections,
                    originalSavedUnsupervisedSelections: loadedSelections,
                }
            }
        ),
        on(selectionActions.setFreeSelection, (state, { name }): SelectionState => {
            if (!name) {
                return {
                    ...state,
                    freeSelection: createNewFreeSelection(),
                }
            }
            return {
                ...state,
                freeSelection: state.savedFreeSelections.get(name) ?? state.freeSelection,
            }
        }),
        on(
            selectionActions.saveNewHierarchicalSelectionRoot,
            (state, { name, segSources }): SelectionState => {
                const newHierarchicalSelectionRoot = createNewHierarchicalSelectionRoot(
                    name,
                    segSources
                )
                return {
                    ...state,
                    hierarchicalSelection: newHierarchicalSelectionRoot,
                }
            }
        ),
        on(selectionActions.setHierarchicalSelectionRoot, (state, { name }): SelectionState => {
            if (state.type === SelectionType.Hierarchical) {
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.savedHierarchicalSelections.get(name),
                        selectedHierarchyName: undefined,
                        unsavedLevel: createNewSelectionBase(),
                    } as HierarchicalSelection,
                }
            } else {
                return {
                    ...state,
                    unsupervisedSelection: {
                        ...state.savedUnsupervisedSelections.get(name),
                        selectedUnsupervisedName: undefined,
                        unsavedLevel: createNewSelectionBase(),
                    } as UnsupervisedSelection,
                }
            }
        }),
        on(selectionActions.setHierarchicalSelectionNode, (state, { name }): SelectionState => {
            if (state.type === SelectionType.Hierarchical) {
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        unsavedLevel: createNewSelectionBase(),
                        selectedHierarchyName: name,
                    },
                }
            } else {
                return {
                    ...state,
                    unsupervisedSelection: {
                        ...state.unsupervisedSelection,
                        unsavedLevel: createNewSelectionBase(),
                        selectedUnsupervisedName: name,
                    },
                }
            }
        }),
        on(
            selectionActions.saveNewAutoCellTypeDetectionRoot,
            (state, { name, segSources }): SelectionState => {
                const newUnsupervisedSelectionRoot = createNewUnsupervisedSelectionRoot(
                    name,
                    segSources
                )
                return {
                    ...state,
                    unsupervisedSelection: newUnsupervisedSelectionRoot,
                }
            }
        ),
        on(selectionActions.clearActiveHierarchicalSelectionRoot, (state): SelectionState => {
            return {
                ...state,
                hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
                displayedOnScatterPlot: [],
            }
        }),
        on(selectionActions.clearActiveUnsupervisedSelectionRoot, (state): SelectionState => {
            return {
                ...state,
                unsupervisedSelection: createNewUnsupervisedSelectionRoot(undefined),
                displayedOnScatterPlot: [],
            }
        }),
        on(selectionActions.setAutoCellTypeDetectionRoot, (state, { name }): SelectionState => {
            return {
                ...state,
                unsupervisedSelection: {
                    ...state.savedUnsupervisedSelections.get(name),
                    selectedUnsupervisedName: undefined,
                    unsavedLevel: createNewSelectionBase(),
                } as any,
            }
        }),
        on(selectionActions.setAutoCellTypeDetectionNode, (state, { name }): SelectionState => {
            return {
                ...state,
                unsupervisedSelection: {
                    ...state.unsupervisedSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedUnsupervisedName: name,
                },
            }
        }),
        on(
            selectionActions.setVisibleSelectionsOnScatterPlot,
            (state, { selectionNames }): SelectionState => {
                let displayedOnScatterPlot: any

                if (state.type == SelectionType.Free) {
                    displayedOnScatterPlot = [...state.savedFreeSelections.values()].filter(
                        selection => selectionNames.includes(selection.name!)
                    )
                }
                if (state.type == SelectionType.Hierarchical) {
                    const flattenedSelections = flattenHierarchicalSelection(
                        state.hierarchicalSelection
                    )
                    displayedOnScatterPlot = flattenedSelections.filter(selection =>
                        selectionNames.includes(selection.name!)
                    )
                }
                if (state.type == SelectionType.Unsupervised) {
                    const flattenedSelections = flattenHierarchicalSelection(
                        state.unsupervisedSelection
                    )
                    displayedOnScatterPlot = flattenedSelections.filter(selection =>
                        selectionNames.includes(selection.name!)
                    )
                }

                return {
                    ...state,
                    displayedOnScatterPlot: displayedOnScatterPlot,
                }
            }
        ),
        on(selectionActions.saveNewFreeSelection, (state, { name }): SelectionState => {
            // Save As must mint a new id; reusing the working selection UUID
            // produces duplicate ids across saved free selections (breaks job refs).
            return {
                ...state,
                freeSelection: {
                    ...state.freeSelection,
                    id: generateSelectionId(),
                    name,
                },
            }
        }),
        on(selectionActions.saveNewHierarchicalSelectionNode, (state, { name }): SelectionState => {
            let selectionRoot: any = {}

            selectionRoot = {
                ...copySelectionRoot(state.hierarchicalSelection),
                unsavedLevel: state.hierarchicalSelection.unsavedLevel,
                selectedHierarchyName: name,
            }

            const selectionName = state.hierarchicalSelection.selectedHierarchyName!

            const parentSelection = findSelectionNodeInRoot(selectionRoot, selectionName)
            if (parentSelection) {
                parentSelection.buckets = removeBucketsFromTarget(
                    parentSelection.buckets,
                    selectionRoot.unsavedLevel.buckets
                )
            }

            const selectionToSave: HierarchicalSelectionNode = {
                ...selectionRoot.unsavedLevel,
                id: generateSelectionId(),
                name: name,
                buckets: ensureBucketsForSegSources(
                    selectionRoot.unsavedLevel.buckets,
                    getHierarchicalRootSegSources(selectionRoot)
                ),
                children: undefined,
            }

            addChildToSelectionRoot(selectionRoot, selectionToSave, selectionName)
            return {
                ...state,
                hierarchicalSelection: selectionRoot,
            }
        }),
        on(selectionActions.deleteHierarchicalSelectionNode, (state): SelectionState => {
            const selectionRoot = copySelectionRoot(state.hierarchicalSelection)
            const selectionName = state.hierarchicalSelection.selectedHierarchyName!
            if (!selectionName) return state
            const selectionToDelete = findSelectionNodeInRoot(selectionRoot, selectionName)
            const parentSelection = findParentSelectionNodeInRoot(selectionRoot, selectionName)

            if (selectionToDelete && parentSelection) {
                parentSelection.buckets = mergeBucketsIntoTarget(
                    parentSelection.buckets,
                    selectionToDelete.buckets
                )
                removeChildrenFromSelectionRoot(selectionRoot, selectionName)
            } else if (selectionToDelete) {
                removeChildrenFromSelectionRoot(selectionRoot, selectionName)
            }

            return {
                ...state,
                hierarchicalSelection: {
                    ...selectionRoot,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(
            selectionActions.deleteHierarchicalSelectionNodeRecursively,
            (state, { groupName, name }): SelectionState => {
                const selectionRoot = copySelectionRoot(
                    state.type === SelectionType.Hierarchical
                        ? state.savedHierarchicalSelections.get(groupName)!
                        : state.savedUnsupervisedSelections.get(groupName)!
                )
                const selectionName = name
                if (!selectionName) return state
                if (!selectionRoot) return state
                const selectionToDelete = findSelectionNodeInRoot(selectionRoot, selectionName)
                const parentSelection = findParentSelectionNodeInRoot(selectionRoot, selectionName)

                if (selectionToDelete && parentSelection) {
                    parentSelection.buckets = mergeBucketsIntoTarget(
                        parentSelection.buckets,
                        selectionToDelete.buckets
                    )
                    removeChildrenFromSelectionRootRecursively(selectionRoot, selectionName)
                } else if (selectionToDelete) {
                    removeChildrenFromSelectionRootRecursively(selectionRoot, selectionName)
                }
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...selectionRoot,
                        unsavedLevel: createNewSelectionBase(),
                        selectedHierarchyName: parentSelection ? parentSelection.name : undefined,
                    },
                }
            }
        ),
        on(selectionActions.deleteAutoCellTypeDetectionNode, (state): SelectionState => {
            const selectionRoot = copySelectionRoot(state.unsupervisedSelection)
            const selectionName = state.unsupervisedSelection.selectedUnsupervisedName!
            if (!selectionName) return state
            const selectionToDelete = findSelectionNodeInRoot(selectionRoot, selectionName)
            const parentSelection = findParentSelectionNodeInRoot(selectionRoot, selectionName)

            if (selectionToDelete && parentSelection) {
                parentSelection.buckets = mergeBucketsIntoTarget(
                    parentSelection.buckets,
                    selectionToDelete.buckets
                )
                removeChildrenFromSelectionRoot(selectionRoot, selectionName)
            } else if (selectionToDelete) {
                removeChildrenFromSelectionRoot(selectionRoot, selectionName)
            }

            return {
                ...state,
                unsupervisedSelection: {
                    ...selectionRoot,
                    unsavedLevel: createNewSelectionBase(),
                    selectedUnsupervisedName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(
            selectionActions.deleteAutoCellTypeDetectionNodeRecursively,
            (state, { groupName, name }): SelectionState => {
                const selectionRoot = copySelectionRoot(
                    state.savedUnsupervisedSelections.get(groupName)!
                )
                const selectionName = name
                if (!selectionName) return state
                if (!selectionRoot) return state
                const selectionToDelete = findSelectionNodeInRoot(selectionRoot, selectionName)
                const parentSelection = findParentSelectionNodeInRoot(selectionRoot, selectionName)

                if (selectionToDelete && parentSelection) {
                    parentSelection.buckets = mergeBucketsIntoTarget(
                        parentSelection.buckets,
                        selectionToDelete.buckets
                    )
                    removeChildrenFromSelectionRootRecursively(selectionRoot, selectionName)
                } else if (selectionToDelete) {
                    removeChildrenFromSelectionRootRecursively(selectionRoot, selectionName)
                }

                return {
                    ...state,
                    unsupervisedSelection: {
                        ...selectionRoot,
                        unsavedLevel: createNewSelectionBase(),
                        selectedUnsupervisedName: parentSelection
                            ? parentSelection.name
                            : undefined,
                    },
                }
            }
        ),
        on(selectionActions.saveNewAutoCellTypeDetectionNode, (state, { name }): SelectionState => {
            let selectionRoot: any = {}
            selectionRoot = {
                ...copySelectionRoot(state.unsupervisedSelection),
                unsavedLevel: state.unsupervisedSelection.unsavedLevel,
                selectedUnsupervisedName: name,
            }

            const selectionName = state.unsupervisedSelection.selectedUnsupervisedName!

            const parentSelection = findSelectionNodeInRoot(selectionRoot, selectionName)
            if (parentSelection) {
                parentSelection.buckets = removeBucketsFromTarget(
                    parentSelection.buckets,
                    selectionRoot.unsavedLevel.buckets
                )
            }

            const selectionToSave: HierarchicalSelectionNode = {
                ...selectionRoot.unsavedLevel,
                id: generateSelectionId(),
                name: name,
                buckets: ensureBucketsForSegSources(
                    selectionRoot.unsavedLevel.buckets,
                    getHierarchicalRootSegSources(selectionRoot)
                ),
                children: undefined,
            }

            addChildToSelectionRoot(selectionRoot, selectionToSave, selectionName)
            return {
                ...state,
                unsupervisedSelection: selectionRoot,
            }
        }),
        on(
            selectionActions.savingFreeSelectionSucceeded,
            (state, { selection }): SelectionState => {
                return {
                    ...state,
                    savedFreeSelections: new Map<string, FreeSelection>([
                        ...state.savedFreeSelections,
                        [selection.name!, { ...selection, modified: false }],
                    ]),
                    freeSelection: { ...selection, modified: false },
                }
            }
        ),
        on(
            selectionActions.savingHierarchicalSelectionRootSucceeded,
            (state, { selection }): SelectionState => {
                const savedHierarchicalSelections = new Map<string, HierarchicalSelectionRoot>([
                    ...state.savedHierarchicalSelections,
                ])
                savedHierarchicalSelections.set(selection.name!, selection)
                return {
                    ...state,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        unsavedLevel: createNewSelectionBase(),
                    },
                    savedHierarchicalSelections: savedHierarchicalSelections,
                }
            }
        ),
        on(selectionActions.savingHierarchicalSelectionRootFailed, (state): SelectionState => {
            const parentSelection = findParentSelectionNodeInRoot(
                state.hierarchicalSelection,
                state.hierarchicalSelection.unsavedLevel.name
            )
            return {
                ...state,
                hierarchicalSelection: {
                    ...state.hierarchicalSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedHierarchyName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(
            selectionActions.savingAutoCellTypeDetectionRootSucceeded,
            (state, { selection }): SelectionState => {
                const savedUnsupervisedSelections = new Map<string, UnsupervisedSelectionRoot>([
                    ...state.savedUnsupervisedSelections,
                ])
                savedUnsupervisedSelections.set(selection.name!, selection)
                return {
                    ...state,
                    unsupervisedSelection: {
                        ...state.unsupervisedSelection,
                        unsavedLevel: createNewSelectionBase(),
                    },
                    savedUnsupervisedSelections: savedUnsupervisedSelections,
                }
            }
        ),
        on(selectionActions.savingAutoCellTypeDetectionRootFailed, (state): SelectionState => {
            const parentSelection = findParentSelectionNodeInRoot(
                state.unsupervisedSelection,
                state.unsupervisedSelection.unsavedLevel.name
            )
            return {
                ...state,
                unsupervisedSelection: {
                    ...state.unsupervisedSelection,
                    unsavedLevel: createNewSelectionBase(),
                    selectedUnsupervisedName: parentSelection ? parentSelection.name : undefined,
                },
            }
        }),
        on(
            selectionActions.renamingFreeSelectionSucceeded,
            (state, { name, newName }): SelectionState => {
                const savedSelections = new Map<string, FreeSelection>([
                    ...state.savedFreeSelections,
                ])
                savedSelections.set(newName, { ...savedSelections.get(name)!, name: newName })
                savedSelections.delete(name)

                const originalSavedSelections = new Map<string, FreeSelection>([
                    ...state.originalSavedFreeSelections,
                ])
                originalSavedSelections.set(newName, {
                    ...originalSavedSelections.get(name)!,
                    name: newName,
                })
                originalSavedSelections.delete(name)

                return {
                    ...state,
                    freeSelection: {
                        ...state.freeSelection,
                        name:
                            state.freeSelection.name === name ? newName : state.freeSelection.name,
                    },
                    savedFreeSelections: savedSelections,
                    originalSavedFreeSelections: originalSavedSelections,
                }
            }
        ),
        on(
            selectionActions.renamingHierarchicalSelectionRootSucceeded,
            (state, { name, newName }): SelectionState => {
                const savedHierarchicalSelections = new Map<string, HierarchicalSelectionRoot>([
                    ...state.savedHierarchicalSelections,
                ])
                savedHierarchicalSelections.set(newName, {
                    ...savedHierarchicalSelections.get(name)!,
                    name: newName,
                })
                savedHierarchicalSelections.delete(name)

                const originalSavedHierarchicalSelections = new Map<
                    string,
                    HierarchicalSelectionRoot
                >([...state.originalSavedHierarchicalSelections])
                originalSavedHierarchicalSelections.set(newName, {
                    ...originalSavedHierarchicalSelections.get(name)!,
                    name: newName,
                })
                originalSavedHierarchicalSelections.delete(name)
                return {
                    ...state,
                    savedHierarchicalSelections: savedHierarchicalSelections,
                    hierarchicalSelection: {
                        ...state.hierarchicalSelection,
                        name:
                            state.hierarchicalSelection.name === name
                                ? newName
                                : state.hierarchicalSelection.name,
                    },
                }
            }
        ),
        on(
            selectionActions.renamingHierarchicalSelectionSucceeded,
            (state, { rootName, name, newName }): SelectionState => {
                const hierarchicalSelection = copySelectionRoot(state.hierarchicalSelection)
                if (hierarchicalSelection.name === rootName) {
                    recurrHierarchicalSelection(hierarchicalSelection.children![0])
                }

                const originalSavedHierarchicalSelections =
                    state.originalSavedHierarchicalSelections.get(rootName)
                recurrHierarchicalSelection(originalSavedHierarchicalSelections!.children![0])

                const savedHierarchicalSelections = state.savedHierarchicalSelections.get(rootName)
                recurrHierarchicalSelection(savedHierarchicalSelections!.children![0])

                return {
                    ...state,
                    originalSavedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>(
                        [
                            ...state.originalSavedHierarchicalSelections,
                            [rootName, originalSavedHierarchicalSelections!],
                        ]
                    ),
                    savedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>([
                        ...state.savedHierarchicalSelections,
                        [rootName, savedHierarchicalSelections!],
                    ]),
                }

                function recurrHierarchicalSelection(
                    parent: HierarchicalSelectionNode
                ): HierarchicalSelectionNode | null {
                    if (!parent.children) return null
                    for (const c of parent.children ?? []) {
                        if (c.name === name) {
                            c.name = newName
                            return c
                        }
                        if (c.children?.length! > 0) {
                            const result = recurrHierarchicalSelection(c)
                            if (result) return result
                        }
                    }
                    return parent
                }
            }
        ),
        on(selectionActions.renamingHierarchicalSelectionRootFailed, (state): SelectionState => {
            return {
                ...state,
                hierarchicalSelection: {
                    ...state.hierarchicalSelection,
                    unsavedLevel: createNewSelectionBase(),
                },
            }
        }),
        on(
            selectionActions.renamingUnsupervisedSelectionRootSucceeded,
            (state, { name, newName }): SelectionState => {
                const savedUnsupervisedSelections = new Map<string, UnsupervisedSelectionRoot>([
                    ...state.savedUnsupervisedSelections,
                ])
                savedUnsupervisedSelections.set(newName, {
                    ...savedUnsupervisedSelections.get(name)!,
                    name: newName,
                })
                savedUnsupervisedSelections.delete(name)

                const originalSavedUnsupervisedSelections = new Map<
                    string,
                    HierarchicalSelectionRoot
                >([...state.originalSavedUnsupervisedSelections])
                originalSavedUnsupervisedSelections.set(newName, {
                    ...originalSavedUnsupervisedSelections.get(name)!,
                    name: newName,
                })
                originalSavedUnsupervisedSelections.delete(name)
                return {
                    ...state,
                    savedUnsupervisedSelections: savedUnsupervisedSelections,
                    unsupervisedSelection: {
                        ...state.unsupervisedSelection,
                        name:
                            state.unsupervisedSelection.name === name
                                ? newName
                                : state.unsupervisedSelection.name,
                    },
                }
            }
        ),
        on(
            selectionActions.renamingUnsupervisedSelectionSucceeded,
            (state, { rootName, name, newName }): SelectionState => {
                const unsupervisedSelection = copySelectionRoot(state.unsupervisedSelection)
                if (unsupervisedSelection.name === rootName) {
                    recurrHierarchicalSelection(unsupervisedSelection.children![0])
                }

                const originalSavedUnsupervisedSelections =
                    state.originalSavedUnsupervisedSelections.get(rootName)
                recurrHierarchicalSelection(originalSavedUnsupervisedSelections!.children![0])

                const savedUnsupervisedSelections = state.savedUnsupervisedSelections.get(rootName)
                recurrHierarchicalSelection(savedUnsupervisedSelections!.children![0])

                return {
                    ...state,
                    originalSavedUnsupervisedSelections: new Map<string, HierarchicalSelectionRoot>(
                        [
                            ...state.originalSavedUnsupervisedSelections,
                            [rootName, originalSavedUnsupervisedSelections!],
                        ]
                    ),
                    savedUnsupervisedSelections: new Map<string, HierarchicalSelectionRoot>([
                        ...state.savedUnsupervisedSelections,
                        [rootName, savedUnsupervisedSelections!],
                    ]),
                }

                function recurrHierarchicalSelection(
                    parent: HierarchicalSelectionNode
                ): HierarchicalSelectionNode | null {
                    if (!parent.children) return null
                    for (const c of parent.children ?? []) {
                        if (c.name === name) {
                            c.name = newName
                            return c
                        }
                        if (c.children?.length! > 0) {
                            const result = recurrHierarchicalSelection(c)
                            if (result) return result
                        }
                    }
                    return parent
                }
            }
        ),
        on(selectionActions.renamingUnsupervisedSelectionRootFailed, (state): SelectionState => {
            return {
                ...state,
                unsupervisedSelection: {
                    ...state.unsupervisedSelection,
                    unsavedLevel: createNewSelectionBase(),
                },
            }
        }),
        on(selectionActions.deletingFreeSelectionSucceeded, (state, { name }): SelectionState => {
            const savedSelections = new Map<string, FreeSelection>([...state.savedFreeSelections])
            savedSelections.delete(name!)
            return {
                ...state,
                savedFreeSelections: savedSelections,
                freeSelection: createNewFreeSelection(),
            }
        }),
        on(
            selectionActions.deletingHierarchicalSelectionRootSucceeded,
            (state, { name }): SelectionState => {
                const savedHierarchicalSelections = new Map<string, HierarchicalSelectionRoot>([
                    ...state.savedHierarchicalSelections,
                ])
                savedHierarchicalSelections.delete(name!)
                return {
                    ...state,
                    savedHierarchicalSelections: savedHierarchicalSelections,
                    hierarchicalSelection: createNewHierarchicalSelectionRoot(undefined),
                }
            }
        ),
        on(
            selectionActions.deletingAutoCellTypeDetectionRootSucceeded,
            (state, { name }): SelectionState => {
                const savedUnsupervisedSelections = new Map<string, UnsupervisedSelectionRoot>([
                    ...state.savedUnsupervisedSelections,
                ])
                savedUnsupervisedSelections.delete(name!)
                return {
                    ...state,
                    savedUnsupervisedSelections: savedUnsupervisedSelections,
                    unsupervisedSelection: createNewUnsupervisedSelectionRoot(undefined),
                }
            }
        ),
        on(selectionActions.setSelectionVisibility, (state, action): SelectionState => {
            if (action.selectionType === SelectionType.Hierarchical) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionVisible(action.visible, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationVisible = action.visible
                return refreshDisplayedOnScatterPlot(
                    updateSavedHierarchicalSelection(selectionRoot, state)
                )
            }

            if (action.selectionType === SelectionType.Unsupervised) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionVisible(action.visible, state)
                }

                const selectionRootFromState = state.savedUnsupervisedSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationVisible = action.visible
                return refreshDisplayedOnScatterPlot(
                    updateSavedHierarchicalSelection(selectionRoot, state)
                )
            }

            if (!action.selectionName) {
                return updateFreeSelectionVisible(action.visible, state)
            }

            const selection = state.savedFreeSelections.get(action.selectionName!)
            if (!selection || !selection.name) {
                return state
            }

            if (action.selectionName === state.freeSelection.name) {
                return updateCurrentAndSavedFreeSelectionVisible(action.visible, selection, state)
            }

            return updateSavedFreeSelectionVisible(action.visible, selection, state)
        }),
        on(selectionActions.setSelectionOpacity, (state, action): SelectionState => {
            if (action.selectionType === SelectionType.Hierarchical) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionOpacity(action.opacity, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationOpacity = action.opacity
                return refreshDisplayedOnScatterPlot(
                    updateSavedHierarchicalSelection(selectionRoot, state)
                )
            }

            if (action.selectionType === SelectionType.Unsupervised) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionOpacity(action.opacity, state)
                }

                const selectionRootFromState = state.savedUnsupervisedSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationOpacity = action.opacity
                return refreshDisplayedOnScatterPlot(
                    updateSavedHierarchicalSelection(selectionRoot, state)
                )
            }
            if (!action.selectionName) {
                return updateFreeSelectionOpacity(action.opacity, state)
            }

            const selection = state.savedFreeSelections.get(action.selectionName!)
            if (!selection || !selection.name) {
                return state
            }

            if (action.selectionName === state.freeSelection.name) {
                return updateCurrentAndSavedFreeSelectionOpacity(action.opacity, selection, state)
            }

            return updateSavedFreeSelectionOpacity(action.opacity, selection, state)
        }),
        on(selectionActions.setSelectionShaderColor, (state, action): SelectionState => {
            if (action.selectionType === SelectionType.Hierarchical) {
                if (!action.selectionName) {
                    return updateHierarchicalSelectionColor(action.color, state)
                }

                const selectionRootFromState = state.savedHierarchicalSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationColor = action.color
                return refreshDisplayedOnScatterPlot(
                    updateSavedHierarchicalSelection(selectionRoot, state)
                )
            }

            if (action.selectionType === SelectionType.Unsupervised) {
                if (!action.selectionName) {
                    return updateUnsupervisedSelectionColor(action.color, state)
                }

                const selectionRootFromState = state.savedUnsupervisedSelections.get(
                    action.selectionRoot!
                )
                if (!selectionRootFromState) return state
                const selectionRoot = copySelectionRoot(selectionRootFromState)
                const selection = findSelectionNodeInRoot(selectionRoot, action.selectionName)
                if (!selection) return state
                selection.annotationColor = action.color
                return refreshDisplayedOnScatterPlot(
                    updateSavedHierarchicalSelection(selectionRoot, state)
                )
            }
            // if name is undefined, update current (unsaved) selection
            if (!action.selectionName) {
                return updateFreeSelectionColor(action.color, state)
            }

            const selection = state.savedFreeSelections.get(action.selectionName!)
            if (!selection || !selection.name) {
                return state
            }

            if (action.selectionName === state.freeSelection.name) {
                return updateCurrentAndSavedFreeSelectionColor(action.color, selection, state)
            }

            return updateSavedFreeSelectionColor(action.color, selection, state)
        }),
        on(caseActions.loadCase, (): SelectionState => createInitialState())
    ),
})

const updateFreeSelectionVisible = (visible: boolean, state: SelectionState): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationVisible: visible,
    },
})

const updateHierarchicalSelectionVisible = (
    visible: boolean,
    state: SelectionState
): SelectionState => {
    if (state.type === SelectionType.Hierarchical) {
        return {
            ...state,
            hierarchicalSelection: {
                ...state.hierarchicalSelection,
                unsavedLevel: {
                    ...state.hierarchicalSelection.unsavedLevel,
                    annotationVisible: visible,
                },
            },
        }
    } else {
        return {
            ...state,
            unsupervisedSelection: {
                ...state.unsupervisedSelection,
                unsavedLevel: {
                    ...state.unsupervisedSelection.unsavedLevel,
                    annotationVisible: visible,
                },
            },
        }
    }
}

const updateCurrentAndSavedFreeSelectionVisible = (
    visible: boolean,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationVisible: visible,
    },
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationVisible: visible }],
    ]),
})

const updateSavedFreeSelectionVisible = (
    visible: boolean,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationVisible: visible }],
    ]),
})

const updateFreeSelectionOpacity = (opacity: number, state: SelectionState): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationOpacity: opacity,
    },
})

const updateHierarchicalSelectionOpacity = (
    opacity: number,
    state: SelectionState
): SelectionState => ({
    ...state,
    hierarchicalSelection: {
        ...state.hierarchicalSelection,
        unsavedLevel: {
            ...state.hierarchicalSelection.unsavedLevel,
            annotationOpacity: opacity,
        },
    },
})

const updateCurrentAndSavedFreeSelectionOpacity = (
    opacity: number,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationOpacity: opacity,
    },
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationOpacity: opacity }],
    ]),
})

const updateSavedFreeSelectionOpacity = (
    opacity: number,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationOpacity: opacity }],
    ]),
})

const updateFreeSelectionColor = (color: string, state: SelectionState): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationColor: color,
    },
})

const updateHierarchicalSelectionColor = (
    color: string,
    state: SelectionState
): SelectionState => ({
    ...state,
    hierarchicalSelection: {
        ...state.hierarchicalSelection,
        unsavedLevel: {
            ...state.hierarchicalSelection.unsavedLevel,
            annotationColor: color,
        },
    },
})

const updateUnsupervisedSelectionColor = (
    color: string,
    state: SelectionState
): SelectionState => ({
    ...state,
    unsupervisedSelection: {
        ...state.unsupervisedSelection,
        unsavedLevel: {
            ...state.unsupervisedSelection.unsavedLevel,
            annotationColor: color,
        },
    },
})

const updateCurrentAndSavedFreeSelectionColor = (
    color: string,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    freeSelection: {
        ...state.freeSelection,
        annotationColor: color,
    },
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationColor: color }],
    ]),
})

const updateSavedFreeSelectionColor = (
    color: string,
    selection: FreeSelection,
    state: SelectionState
): SelectionState => ({
    ...state,
    savedFreeSelections: new Map<string, FreeSelection>([
        ...state.savedFreeSelections,
        [selection.name!, { ...selection, annotationColor: color }],
    ]),
})

const updateSavedHierarchicalSelection = (
    selectionRoot: HierarchicalSelectionRoot,
    state: SelectionState
): SelectionState => {
    if (selectionRoot.type === SelectionType.Hierarchical) {
        return {
            ...state,
            hierarchicalSelection: {
                ...state.hierarchicalSelection,
                children: selectionRoot.children,
            },
            savedHierarchicalSelections: new Map<string, HierarchicalSelectionRoot>([
                ...state.savedHierarchicalSelections,
                [selectionRoot.name!, selectionRoot],
            ]),
        }
    } else {
        return {
            ...state,
            unsupervisedSelection: {
                ...state.unsupervisedSelection,
                children: selectionRoot.children,
            },
            savedUnsupervisedSelections: new Map<string, UnsupervisedSelectionRoot>([
                ...state.savedUnsupervisedSelections,
                [selectionRoot.name!, selectionRoot],
            ]),
        }
    }
}

const copySelectionRoot = (selection: HierarchicalSelectionRoot): HierarchicalSelectionRoot => {
    if (!selection.children) return { ...selection }
    return {
        ...selection,
        children: selection.children?.map(child => copySelectionNode(child)),
    }
}

const copySelectionNode = (selection: HierarchicalSelectionNode): HierarchicalSelectionNode => {
    if (!selection.children) {
        return { ...selection, buckets: copyBuckets(selection.buckets) }
    }
    return {
        ...selection,
        buckets: copyBuckets(selection.buckets),
        children: selection.children?.map(child => copySelectionNode(child)),
    }
}

const updateSelectionRootWithQueryParams = (
    selectionRoot: HierarchicalSelectionRoot,
    queryParams: HierarchicalSelectionRootQueryParam
): void => {
    if (!selectionRoot.children) return
    selectionRoot.children.forEach(child => {
        updateSelectionNodeWithQueryParams(child, queryParams)
    })
}

const updateSelectionNodeWithQueryParams = (
    selectionNode: HierarchicalSelectionNode,
    queryParams: HierarchicalSelectionRootQueryParam
): void => {
    const queryParamsNode = findSelectionNodeInRootQueryParam(queryParams, selectionNode.name)

    selectionNode.annotationVisible = queryParamsNode
        ? queryParamsNode.annotationVisible
        : selectionNode.annotationVisible
    selectionNode.annotationOpacity = queryParamsNode
        ? queryParamsNode.annotationOpacity
        : selectionNode.annotationOpacity
    selectionNode.annotationColor = queryParamsNode
        ? queryParamsNode.annotationColor
        : selectionNode.annotationColor

    if (!selectionNode.children) return
    selectionNode.children.forEach(child => {
        updateSelectionNodeWithQueryParams(child, queryParams)
    })
}

const findSelectionNodeInRootQueryParam = (
    selection: HierarchicalSelectionRootQueryParam,
    name: string | undefined
): HierarchicalSelectionNodeQueryParam | null => {
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNodeQueryParam(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

const findSelectionNodeInNodeQueryParam = (
    selection: HierarchicalSelectionNodeQueryParam,
    name: string | undefined
): HierarchicalSelectionNodeQueryParam | null => {
    if (selection.name === name) {
        return selection
    }
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNodeQueryParam(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

const flattenHierarchicalSelection = (
    root: HierarchicalSelection | UnsupervisedSelection
): HierarchicalSelectionNode[] => {
    const result: HierarchicalSelectionNode[] = []

    const traverse = (node: HierarchicalSelectionNode) => {
        result.push(node)
        if (node.children) {
            node.children.forEach(traverse)
        }
    }

    if (root.children) {
        root.children.forEach(traverse)
    }

    return result
}

/** Keep displayedOnScatterPlot in sync with live tree nodes after visibility/color edits. */
const refreshDisplayedOnScatterPlot = (state: SelectionState): SelectionState => {
    if (state.displayedOnScatterPlot.length === 0) return state
    const names = new Set(
        state.displayedOnScatterPlot.map(s => s.name).filter((n): n is string => !!n)
    )
    if (names.size === 0) return state

    if (state.type === SelectionType.Hierarchical) {
        return {
            ...state,
            displayedOnScatterPlot: flattenHierarchicalSelection(
                state.hierarchicalSelection
            ).filter(s => !!s.name && names.has(s.name)),
        }
    }
    if (state.type === SelectionType.Unsupervised) {
        return {
            ...state,
            displayedOnScatterPlot: flattenHierarchicalSelection(
                state.unsupervisedSelection
            ).filter(s => !!s.name && names.has(s.name)),
        }
    }
    if (state.type === SelectionType.Free) {
        return {
            ...state,
            displayedOnScatterPlot: [...state.savedFreeSelections.values()].filter(
                s => !!s.name && names.has(s.name)
            ),
        }
    }
    return state
}

export const { reducer } = selectionFeature
